import { MutableRefObject, useEffect, useCallback } from 'react';
import cytoscape from 'cytoscape';
import { useNodeHighlighting } from './useNodeHighlighting';
import { Node, NodeType } from '../types/cytoscape';

import { useSetFilterConditionAtom } from '@/features/analyze/atoms/filterCondition.atom';

// 追加のプロパティを受け取るよう拡張
interface UseCytoscapeEventsProps {
  cytoscapeCoreRefObject: MutableRefObject<cytoscape.Core | null>;
  showTooltip: (node_data: Node, position: { x: number; y: number }) => void;
  hideTooltip: () => void;
  // 監視すべき状態を追加
  toggleStates?: {
    showOnlySocialIssues?: boolean;
    hideTagsWithoutDocuments?: boolean;
    isInfoPanelVisible?: boolean;
  };
}

export const useCytoscapeEvents = ({
  cytoscapeCoreRefObject,
  showTooltip,
  hideTooltip,
  toggleStates = {}, // デフォルト値を設定
}: UseCytoscapeEventsProps) => {
  const { highlightNodeAndConnections, removeHighlighting } =
    useNodeHighlighting();

  const setFilterCondition = useSetFilterConditionAtom();

  // 背景クリックハンドラを追加
  const handleBackgroundClick = useCallback(
    (event: cytoscape.EventObject) => {
      const cy = cytoscapeCoreRefObject.current;
      if (!cy) return;

      // イベントターゲットが背景（非ノード要素）であることを確認
      if (event.target === cy || event.target.isEdge()) {
        // すべての選択を解除
        cy.elements().unselect();

        hideTooltip();

        // ハイライトを削除
        removeHighlighting(cytoscapeCoreRefObject);
      }
    },
    [cytoscapeCoreRefObject, removeHighlighting]
  );

  const handleNodeClick = useCallback(
    (event: cytoscape.EventObject) => {
      const cy = cytoscapeCoreRefObject.current;
      if (!cy) return;

      if (event.target && event.target.isNode()) {
        const node: cytoscape.NodeSingular = event.target;
        // 要素の位置を取得
        const position = event.renderedPosition || node.renderedPosition();

        highlightNodeAndConnections(cytoscapeCoreRefObject, node.id());

        // ツールチップを表示
        showTooltip(node.data(), {
          x: position.x + 10, // オフセットを追加してカーソルと重ならないように
          y: position.y + 10,
        });
      } else {
        // ノード以外の要素がクリックされた場合はハイライトを削除
        removeHighlighting(cytoscapeCoreRefObject);
      }
    },
    [
      cytoscapeCoreRefObject,
      highlightNodeAndConnections,
      removeHighlighting,
      setFilterCondition,
    ]
  );

  useEffect(() => {
    const cy = cytoscapeCoreRefObject.current;
    if (!cy) return;

    // すべてのイベントを削除
    cy.removeAllListeners();

    cy.on('tap', handleBackgroundClick);
    cy.on('tap', 'node', handleNodeClick);

    // ノード位置を追跡するための変数を用意
    const dragPositions = new Map<string, { x: number; y: number }>();

    // テーマノード移動時に接続ノードも移動
    cy.on('dragfree', 'node', (event) => {
      const node = event.target;
      if (node.data('nodeType') !== NodeType.THEME) return;

      // 保存した座標を取得
      const oldPosition = dragPositions.get(node.id());
      if (!oldPosition) return;
      const newPosition = node.position();
      const dx = newPosition.x - oldPosition.x;
      const dy = newPosition.y - oldPosition.y;

      // テーマノード以外で接続しているノードを取得
      const themeNodes = cy.nodes(`[nodeType = "${NodeType.THEME}"]`);
      const connectedNodes = node.neighborhood('node').not(themeNodes);

      connectedNodes.forEach((connectedNode: cytoscape.NodeSingular) => {
        const pos = connectedNode.position();
        connectedNode.position({
          x: pos.x + dx,
          y: pos.y + dy,
        });
      });

      // dragfree後にoldPositionをクリア
      node.scratch('_oldPosition', null);
    });

    // ドラッグ開始時に位置を保存
    cy.on('grab', 'node', (event) => {
      const node = event.target;
      if (node.data('nodeType') === NodeType.THEME) {
        // 値をコピーして保存
        const pos = node.position();
        dragPositions.set(node.id(), { x: pos.x, y: pos.y });
      }
    });
  }, [
    cytoscapeCoreRefObject,
    handleNodeClick,
    handleBackgroundClick,
    // toggleStatesのオブジェクト自体ではなく、個々の値を依存配列に明示的に含める
    toggleStates.showOnlySocialIssues,
    toggleStates.hideTagsWithoutDocuments,
    toggleStates.isInfoPanelVisible,
  ]);
};
